/**
 * Ministers Service
 * Handles all minister-related database operations
 */

import { supabase, type PaginatedResponse, type SortOption } from './supabaseClient';
import { Minister } from '@/models/minister';
import { Database } from '@/types/supabase';
import { NotFoundError, DatabaseError } from '@/lib/errors';
import { withMinistersLimit } from '@/lib/queryHelpers';
import { PAGINATION } from '@/constants';
import type { InactiveReason, MinisterHistoryEvent } from '@/features/ministers/types';

// Type helpers for inserts and updates
type MinisterInsert = Database['public']['Tables']['ministers']['Insert'];
type MinisterUpdate = Database['public']['Tables']['ministers']['Update'];

/**
 * Filters for minister queries
 */
export interface MinisterFilters {
  search?: string;
  regionId?: string;
  districtId?: string;
  degreeId?: string;
  isActive?: boolean;
}

/**
 * Minister with availability status for credential requests
 */
export interface MinisterAvailability extends Minister {
  availability_status: 'AVAILABLE' | 'TAKEN' | 'ADDED';
  current_request_codes: string[];
}

/**
 * Result from get_ministers_with_availability RPC function
 */
interface MinisterAvailabilityRPC {
  id: string;
  adb_code: string;
  full_name: string;
  dni: string;
  degree_id: string | null;
  degree_name: string | null;
  district_id: string | null;
  district_name: string | null;
  district_code: string | null;
  region_id: string | null;
  region_name: string | null;
  region_code: string | null;
  availability_status: 'AVAILABLE' | 'TAKEN';
  current_request_codes: string[];
}

/**
 * Get ministers with year-specific availability using optimized database function
 * Fetches all results in batches to overcome Supabase's row limit
 * @param options - Filter options for district, region, year, and credential type
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinistersWithAvailability(options: {
  districtId?: string;
  regionId?: string;
  year?: number;
  credentialType?: 'MINISTERIAL' | 'INSTITUTION' | 'FUNCTION';
}): Promise<MinisterAvailability[]> {
  try {
    const BATCH_SIZE = 1000;
    let allMinisters: MinisterAvailabilityRPC[] = [];
    let offset = 0;
    let hasMore = true;

    // Fetch in batches until we get all results
    while (hasMore) {
      const { data, error } = await supabase.rpc('get_ministers_with_availability', {
        p_district_id: options.districtId || null,
        p_region_id: options.regionId || null,
        p_year: options.year || null,
        p_credential_type: options.credentialType || null,
        p_limit: BATCH_SIZE,
        p_offset: offset,
      } as unknown as undefined);

      if (error) throw error;

      const batch = data as MinisterAvailabilityRPC[];
      allMinisters = allMinisters.concat(batch);

      // If we got fewer results than the batch size, we've reached the end
      hasMore = batch.length === BATCH_SIZE;
      offset += BATCH_SIZE;
    }

    // Map RPC result to MinisterAvailability format
    return allMinisters.map(m => ({
      id: m.id,
      adb_code: m.adb_code,
      full_name: m.full_name,
      dni: m.dni,
      degree_id: m.degree_id,
      district_id: m.district_id,
      region_id: m.region_id,
      is_active: true, // Function only returns active ministers
      availability_status: m.availability_status,
      current_request_codes: m.current_request_codes || [], // Include request codes
      // Add degree, district, region objects for compatibility
      degree: m.degree_id ? { id: m.degree_id, name: m.degree_name || '' } : undefined,
      district: m.district_id ? { 
        id: m.district_id, 
        name: m.district_name || '', 
        code: m.district_code || '' 
      } : undefined,
      region: m.region_id ? { 
        id: m.region_id, 
        name: m.region_name || '', 
        code: m.region_code || '' 
      } : undefined,
      // Default values for other Minister fields
      first_name: null,
      last_name: null,
      second_last_name: null,
      position: null,
      email: null,
      cell_phone: null,
      address: null,
      address_city: null,
      marriage_last_name: null,
      address_zone: null,
      office_phone: null,
      created_at: new Date().toISOString(),
      updated_at: null,
      created_by: null,
      updated_by: null,
      profile_picture: null,
      profile_picture_filename: null,
    } as MinisterAvailability));
  } catch (error) {
    throw new DatabaseError(
      `Failed to fetch ministers with availability for district: ${options.districtId}, region: ${options.regionId}, year: ${options.year}, credentialType: ${options.credentialType}`,
      error
    );
  }
}

/**
 * Get paginated list of ministers with filters and sorting
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinistersPaginated(
  page: number = 1,
  pageSize: number = PAGINATION.DEFAULT_PAGE_SIZE,
  filters?: MinisterFilters,
  sort?: SortOption
): Promise<PaginatedResponse<Minister>> {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('ministers')
      .select(`
        *,
        degree:degrees(id, name),
        district:districts!ministers_district_id_fkey(id, name, code),
        region:regions!ministers_region_id_fkey(id, name, code)
      `, { count: 'exact' });

    // Apply filters
    if (filters?.search) {
      const searchTokens = filters.search.trim().split(/\s+/).filter(token => token.length > 0);
      
      if (searchTokens.length === 1) {
        const token = searchTokens[0];
        // Normalize token by removing dashes for flexible code matching
        const normalizedToken = token.replace(/-/g, '');
        
        // Build OR conditions - include both original token and normalized for codes
        const orConditions = [
          `first_name.ilike.%${token}%`,
          `last_name.ilike.%${token}%`,
          `second_last_name.ilike.%${token}%`,
          `marriage_last_name.ilike.%${token}%`,
          `adb_code.ilike.%${token}%`,
          `dni.ilike.%${token}%`,
          `email.ilike.%${token}%`,
          `full_name.ilike.%${token}%`
        ];
        
        // If token is numeric (no dashes), also search for codes that match when dashes are removed
        // e.g., "1234" should match "12-034", "12-340", etc.
        if (/^\d+$/.test(normalizedToken)) {
          // Add pattern for codes like XX-XXX where removing dash matches the search
          // This is a workaround since Supabase doesn't support REPLACE in OR clause
          // We'll add additional LIKE patterns to catch common formats
          const patterns = [];
          if (normalizedToken.length >= 2) {
            // Try to match XX-... pattern
            patterns.push(`${normalizedToken.slice(0, 2)}-${normalizedToken.slice(2)}%`);
          }
          if (normalizedToken.length >= 4) {
            // Try to match XX-XXX pattern more specifically
            patterns.push(`${normalizedToken.slice(0, 2)}-${normalizedToken.slice(2, 5)}%`);
          }
          patterns.forEach(pattern => {
            orConditions.push(`adb_code.ilike.${pattern}`);
          });
        }
        
        query = query.or(orConditions.join(','));
      } else {
        // Multiple tokens: use AND logic by chaining .filter() with textSearch
        // Each token must appear in full_name for AND behavior
        searchTokens.forEach(token => {
          query = query.filter('full_name', 'ilike', `%${token}%`);
        });
      }
    }

    if (filters?.regionId) {
      query = query.eq('region_id', filters.regionId);
    }

    if (filters?.districtId) {
      query = query.eq('district_id', filters.districtId);
    }

    if (filters?.degreeId) {
      query = query.eq('degree_id', filters.degreeId);
    }

    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }

    // Apply sorting
    if (sort?.field) {
      query = query.order(sort.field, { ascending: sort.direction === 'asc' });
    } else {
      query = query.order('adb_code', { ascending: true });
    }

    // Apply pagination
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;

    return {
      data: (data || []) as unknown as Minister[],
      count: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  } catch (error) {
    throw new DatabaseError('Failed to fetch ministers', error);
  }
}

/**
 * Get all ministers (no pagination)
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllMinisters(): Promise<Minister[]> {
  try {
    const query = supabase
      .from('ministers')
      .select(`
        *,
        degree:degrees(id, name),
        district:districts!ministers_district_id_fkey(id, name, code),
        region:regions!ministers_region_id_fkey(id, name, code)
      `)
      .eq('is_active', true)
      .order('adb_code', { ascending: true });

    // Apply ministers limit (prevents hitting default 1000-row limit)
    const { data, error } = await withMinistersLimit(query);

    if (error) throw error;
    return data || [];
  } catch (error) {
    throw new DatabaseError('Failed to fetch all ministers', error);
  }
}

/**
 * Fetch all ministers (active + inactive) for client-side caching.
 * Excludes profile_picture binary to keep the payload small.
 * Batches in 1000-row chunks to bypass PostgREST's default row cap.
 * @throws {DatabaseError} If the database query fails
 */
export async function getAllMinistersForCache(): Promise<Minister[]> {
  try {
    const BATCH_SIZE = 1000;
    const all: Minister[] = [];
    let offset = 0;

    for (;;) {
      const { data, error } = await supabase
        .from('ministers')
        .select(`
          id, adb_code, full_name, first_name, last_name,
          second_last_name, marriage_last_name, dni,
          degree_id, district_id, region_id, position, is_active,
          email, cell_phone, office_phone, address, address_city, address_zone,
          inactive_reason_code, inactive_at, profile_picture_filename,
          created_at, updated_at, created_by, updated_by,
          degree:degrees(id, name),
          district:districts!ministers_district_id_fkey(id, name, code),
          region:regions!ministers_region_id_fkey(id, name, code)
        `)
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true })
        .range(offset, offset + BATCH_SIZE - 1);

      if (error) throw error;

      const batch = (data as unknown as Minister[]) ?? [];
      all.push(...batch);

      if (batch.length < BATCH_SIZE) break;
      offset += BATCH_SIZE;
      if (offset > 50000) {
        console.warn('getAllMinistersForCache: reached 50k safety limit');
        break;
      }
    }

    return all;
  } catch (error) {
    throw new DatabaseError('Failed to fetch all ministers for cache', error);
  }
}

/**
 * Get a minister by ID
 * @throws {NotFoundError} If minister is not found
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinisterById(id: string): Promise<Minister | null> {
  try {
    const { data, error } = await supabase
      .from('ministers')
      .select(`
        *,
        degree:degrees(id, name),
        district:districts!ministers_district_id_fkey(id, name, code),
        region:regions!ministers_region_id_fkey(id, name, code)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Minister with ID ${id} not found`);
      }
      throw error;
    }

    return data as unknown as Minister;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to fetch minister with ID ${id}`, error);
  }
}

/**
 * Get ministers by region
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinistersByRegion(regionId: string): Promise<Minister[]> {
  try {
    const { data, error } = await supabase
      .from('ministers')
      .select(`
        *,
        degree:degrees(id, name),
        district:districts!ministers_district_id_fkey(id, name, code),
        region:regions!ministers_region_id_fkey(id, name, code)
      `)
      .eq('region_id', regionId)
      .eq('is_active', true)
      .order('adb_code', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as Minister[];
  } catch (error) {
    throw new DatabaseError(`Failed to fetch ministers for region ${regionId}`, error);
  }
}

/**
 * Get ministers by district
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinistersByDistrict(districtId: string): Promise<Minister[]> {
  try {
    const { data, error } = await supabase
      .from('ministers')
      .select(`
        *,
        degree:degrees(id, name),
        district:districts!ministers_district_id_fkey(id, name, code),
        region:regions!ministers_region_id_fkey(id, name, code)
      `)
      .eq('district_id', districtId)
      .eq('is_active', true)
      .order('adb_code', { ascending: true });

    if (error) throw error;
    return (data || []) as unknown as Minister[];
  } catch (error) {
    throw new DatabaseError(`Failed to fetch ministers for district ${districtId}`, error);
  }
}

/**
 * Get ministers by region with availability status for credential requests
 * @deprecated Use getMinistersWithAvailability instead for better performance
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinistersAvailabilityByRegion(
  regionId: string,
  requestYear: number
): Promise<MinisterAvailability[]> {
  // Use the new optimized function
  return getMinistersWithAvailability({
    regionId,
    year: requestYear,
  });
}

/**
 * Create a new minister
 * @throws {DatabaseError} If the database operation fails
 */
export async function createMinister(minister: MinisterInsert): Promise<Minister> {
  try {
    const { data, error } = await supabase
      .from('ministers')
      .insert(minister as never)
      .select()
      .single();

    if (error) throw error;
    return data as unknown as Minister;
  } catch (error) {
    throw new DatabaseError('Failed to create minister', error);
  }
}

/**
 * Update an existing minister
 * @throws {NotFoundError} If minister is not found
 * @throws {DatabaseError} If the database operation fails
 */
export async function updateMinister(id: string, updates: MinisterUpdate): Promise<Minister> {
  try {
    const { data, error } = await supabase
      .from('ministers')
      .update({ ...updates, updated_at: new Date().toISOString() } as never)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        throw new NotFoundError(`Minister with ID ${id} not found`);
      }
      throw error;
    }

    return data as unknown as Minister;
  } catch (error) {
    if (error instanceof NotFoundError) throw error;
    throw new DatabaseError(`Failed to update minister with ID ${id}`, error);
  }
}

/**
 * Soft delete a minister (sets is_active to false)
 * @throws {DatabaseError} If the database operation fails
 */
export async function deleteMinister(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('ministers')
      .update({ is_active: false, updated_at: new Date().toISOString() } as never)
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to delete minister with ID ${id}`, error);
  }
}

export interface MinisterLinkedRecords {
  authorityPositions: number;
  credentialRequests: number;
  eventParticipations: number;
  eventGuests: number;
  institutionAuthorities: number;
  institutionMemberships: number;
  total: number;
}

/**
 * Count records in NO-ACTION FK tables that would block a hard delete.
 * minister_history cascades; districts/regions SET NULL — those are safe.
 */
export async function checkMinisterLinkedRecords(id: string): Promise<MinisterLinkedRecords> {
  try {
    const [ap, crm, ep, eg, iap, im] = await Promise.all([
      supabase.from('authority_positions').select('id', { count: 'exact', head: true }).eq('minister_id', id),
      supabase.from('credential_request_ministers').select('minister_id', { count: 'exact', head: true }).eq('minister_id', id),
      supabase.from('event_participants').select('id', { count: 'exact', head: true }).eq('minister_id', id),
      supabase.from('event_guests').select('id', { count: 'exact', head: true }).eq('representing_minister_id', id),
      supabase.from('institution_authority_positions').select('id', { count: 'exact', head: true }).eq('minister_id', id),
      supabase.from('institution_memberships').select('id', { count: 'exact', head: true }).eq('minister_id', id),
    ]);

    const authorityPositions   = ap.count  ?? 0;
    const credentialRequests   = crm.count ?? 0;
    const eventParticipations  = ep.count  ?? 0;
    const eventGuests          = eg.count  ?? 0;
    const institutionAuthorities = iap.count ?? 0;
    const institutionMemberships = im.count  ?? 0;

    return {
      authorityPositions,
      credentialRequests,
      eventParticipations,
      eventGuests,
      institutionAuthorities,
      institutionMemberships,
      total: authorityPositions + credentialRequests + eventParticipations + eventGuests + institutionAuthorities + institutionMemberships,
    };
  } catch (error) {
    throw new DatabaseError(`Failed to check linked records for minister ${id}`, error);
  }
}

/**
 * Permanently delete a minister row.
 * Only safe to call after checkMinisterLinkedRecords returns total === 0.
 * minister_history cascades; districts/regions SET NULL automatically.
 */
export async function hardDeleteMinister(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('ministers').delete().eq('id', id);
    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to hard delete minister with ID ${id}`, error);
  }
}

/**
 * Fetch all inactive reasons ordered by sort_order
 * @throws {DatabaseError} If the database query fails
 */
export async function getInactiveReasons(): Promise<InactiveReason[]> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from('inactive_reasons')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return (data || []) as InactiveReason[];
  } catch (error) {
    throw new DatabaseError('Failed to fetch inactive reasons', error);
  }
}

/**
 * Mark a minister as inactive with a mandatory reason, optional date and notes.
 * Updates ministers table and inserts a history record.
 * @throws {DatabaseError} If any database operation fails
 */
export async function inactivateMinister(
  ministerId: string,
  reasonCode: string,
  eventDate: string,
  userId: string,
  notes?: string
): Promise<void> {
  try {
    const { error: updateError } = await supabase
      .from('ministers')
      .update({
        is_active: false,
        inactive_reason_code: reasonCode,
        inactive_at: eventDate,
        updated_at: new Date().toISOString(),
      } as never)
      .eq('id', ministerId);

    if (updateError) throw updateError;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: historyError } = await (supabase as any)
      .from('minister_history')
      .insert({
        minister_id: ministerId,
        event_type: 'status_change',
        event_date: eventDate,
        status_from: 'active',
        status_to: 'inactive',
        inactive_reason_code: reasonCode,
        notes: notes || null,
        created_by: userId,
      });

    if (historyError) throw historyError;
  } catch (error) {
    throw new DatabaseError(`Failed to inactivate minister with ID ${ministerId}`, error);
  }
}

/**
 * Reactivate an inactive minister.
 * Clears inactive_reason_code and inactive_at, inserts a history record.
 * @throws {DatabaseError} If any database operation fails
 */
export async function reactivateMinister(
  ministerId: string,
  eventDate: string,
  userId: string,
  notes?: string
): Promise<void> {
  try {
    const { error: updateError } = await supabase
      .from('ministers')
      .update({
        is_active: true,
        inactive_reason_code: null,
        inactive_at: null,
        updated_at: new Date().toISOString(),
      } as never)
      .eq('id', ministerId);

    if (updateError) throw updateError;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: historyError } = await (supabase as any)
      .from('minister_history')
      .insert({
        minister_id: ministerId,
        event_type: 'status_change',
        event_date: eventDate,
        status_from: 'inactive',
        status_to: 'active',
        inactive_reason_code: null,
        notes: notes || null,
        created_by: userId,
      });

    if (historyError) throw historyError;
  } catch (error) {
    throw new DatabaseError(`Failed to reactivate minister with ID ${ministerId}`, error);
  }
}

/**
 * Fetch full history for a minister (all event types), newest first.
 * @throws {DatabaseError} If the database query fails
 */
export async function getMinisterHistory(ministerId: string): Promise<MinisterHistoryEvent[]> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from('minister_history')
      .select(`
        *,
        inactive_reason:inactive_reasons(code, label, sort_order),
        degree_from_relation:degrees!minister_history_degree_from_fkey(id, name),
        degree_to_relation:degrees!minister_history_degree_to_fkey(id, name),
        district_from_relation:districts!minister_history_district_from_fkey(id, name),
        district_to_relation:districts!minister_history_district_to_fkey(id, name),
        region_from_relation:regions!minister_history_region_from_fkey(id, name),
        region_to_relation:regions!minister_history_region_to_fkey(id, name)
      `)
      .eq('minister_id', ministerId)
      .order('event_date', { ascending: false });

    if (error) throw error;

    const events = (data || []) as unknown as MinisterHistoryEvent[];

    // Batch-fetch user names for created_by IDs (no FK so we query separately)
    const userIds = [...new Set(events.map(e => e.created_by).filter(Boolean))] as string[];
    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, full_name')
        .in('id', userIds);

      if (users) {
        const nameMap = new Map((users as { id: string; full_name: string }[]).map(u => [u.id, u.full_name]));
        events.forEach(e => {
          if (e.created_by) e.created_by_name = nameMap.get(e.created_by) ?? null;
        });
      }
    }

    return events;
  } catch (error) {
    throw new DatabaseError(`Failed to fetch history for minister ${ministerId}`, error);
  }
}

/**
 * Log a degree change for a minister.
 * @throws {DatabaseError} If the database operation fails
 */
export async function logDegreeUpdate(
  ministerId: string,
  degreeFrom: string | null,
  degreeTo: string,
  eventDate: string,
  userId: string,
  notes?: string
): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('minister_history')
      .insert({
        minister_id: ministerId,
        event_type: 'degree_update',
        event_date: eventDate,
        degree_from: degreeFrom || null,
        degree_to: degreeTo,
        notes: notes || null,
        created_by: userId,
      });

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to log degree update for minister ${ministerId}`, error);
  }
}

/**
 * Change a minister's degree and log the event in history.
 * Single atomic action: updates ministers.degree_id + inserts minister_history record.
 */
export async function changeDegree(
  ministerId: string,
  degreeFrom: string | null,
  degreeTo: string,
  eventDate: string,
  userId: string,
  notes?: string
): Promise<void> {
  try {
    const { error: updateError } = await supabase
      .from('ministers')
      .update({ degree_id: degreeTo, updated_at: new Date().toISOString(), updated_by: userId } as never)
      .eq('id', ministerId);
    if (updateError) throw updateError;
    await logDegreeUpdate(ministerId, degreeFrom, degreeTo, eventDate, userId, notes);
  } catch (error) {
    throw new DatabaseError(`Failed to change degree for minister ${ministerId}`, error);
  }
}

/**
 * Transfer a minister to a new district/region and log the event in history.
 * Single atomic action: updates ministers.district_id + region_id + inserts minister_history record.
 */
export async function transferMinister(
  ministerId: string,
  districtFrom: string | null,
  districtTo: string,
  regionFrom: string | null,
  regionTo: string,
  eventDate: string,
  userId: string,
  notes?: string
): Promise<void> {
  try {
    const { error: updateError } = await supabase
      .from('ministers')
      .update({
        district_id: districtTo,
        region_id: regionTo,
        updated_at: new Date().toISOString(),
        updated_by: userId,
      } as never)
      .eq('id', ministerId);
    if (updateError) throw updateError;
    await logTransfer(ministerId, districtFrom, districtTo, regionFrom, regionTo, eventDate, userId, notes);
  } catch (error) {
    throw new DatabaseError(`Failed to transfer minister ${ministerId}`, error);
  }
}

/**
 * Log a district/region transfer for a minister.
 * @throws {DatabaseError} If the database operation fails
 */
export async function logTransfer(
  ministerId: string,
  districtFrom: string | null,
  districtTo: string | null,
  regionFrom: string | null,
  regionTo: string | null,
  eventDate: string,
  userId: string,
  notes?: string
): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase as any)
      .from('minister_history')
      .insert({
        minister_id: ministerId,
        event_type: 'transfer',
        event_date: eventDate,
        district_from: districtFrom || null,
        district_to: districtTo || null,
        region_from: regionFrom || null,
        region_to: regionTo || null,
        notes: notes || null,
        created_by: userId,
      });

    if (error) throw error;
  } catch (error) {
    throw new DatabaseError(`Failed to log transfer for minister ${ministerId}`, error);
  }
}
